import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useAuth0 } from '@auth0/auth0-react';
import endOfWeek from 'date-fns/endOfWeek';
import startOfWeek from 'date-fns/startOfWeek';
import { useTranslation } from 'react-i18next';

import {
  FBoxContainer,
  FHAutocomplete,
  FProgressBar,
  FDatePicker,
  FHWeekDatesPicker,
} from '../components/base';
import { clientActions, clientSupervisionActions, sessionActions } from '../store';
import { FORTA_BLACK, FORTA_BLUE, FORTA_WHITE } from '../utils/colors';
import { getThemeColor } from '../utils/common';
import { areUserRolesIn } from '../helpers';
import PageContainer from '../components/layout/PageContainer';
import SessionNoteItem from '../components/layout/dashboard/SessionNoteItem';
import SupervisionItem from '../components/layout/dashboard/SupervisionItem';
import { Roles, SupervisionType, Themes } from '../constants';

import type {
  IOption,
  IClient,
  TabPanelProps,
  IClientSupervision,
  IBTSession,
} from '../utils/types';
import type { RootState } from '../store';
import type { ChangeEvent, HTMLAttributes } from 'react';

enum HistoricalNotesTabEnum {
  Sessions = 0,
  ClientSN = 1,
}

function HistoricalNotes() {
  const theme = useTheme();
  const { t } = useTranslation('historicalNotes');
  const AllClientOption = { id: -1, label: t('allClients') };
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isTablet = useMediaQuery(theme.breakpoints.between(0, 900));
  const { user } = useSelector((state: RootState) => state.auth);
  const { filteredList: sessions = [], loading: sessionLoading } = useSelector(
    (state: RootState) => state.sessions,
  );
  const { list: clientSupervisions = [], loading: clientSNLoading } = useSelector(
    (state: RootState) => state.clientSupervisions,
  );

  const { list: clients = [], loading: clientLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const [calendarDate, setCalendarDate] = useState<{ startDate: Date; endDate: Date }>({
    startDate: startOfWeek(new Date()),
    endDate: endOfWeek(new Date()),
  });
  const [date, setDate] = useState<Date>(new Date());
  const [selectedClient, setSelectedClient] = useState<IOption | null>(
    areUserRolesIn(user?.roles, [Roles.Admin, Roles.ClientSuccessManager]) ? null : AllClientOption,
  );

  const matches = useMediaQuery('(max-width:900px)');
  const calendarStyle = matches ? { width: '100%' } : { width: '445px' };

  // Tabs states
  const [value, setValue] = useState<number>(0);

  const clientListWithAllClientOption = useMemo(() => {
    return [
      AllClientOption,
      ...clients.map((client: IClient) => ({
        id: client.id,
        label: `${client.first_name} ${client.last_name}`,
      })),
    ];
  }, [t, clients]);

  useEffect(() => {
    if (selectedClient?.id === AllClientOption.id) {
      setSelectedClient(AllClientOption);
    }
  }, [t]);

  useEffect(() => {
    dispatch(clientActions.getList({ getAccessTokenSilently }));
    return () => {
      dispatch(sessionActions.clearSession());
      dispatch(clientSupervisionActions.clearState());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedClient) {
      let params;
      if (selectedClient.id !== AllClientOption.id) {
        params = {
          client_id: selectedClient.id,
          startDate: calendarDate.startDate,
          endDate: calendarDate.endDate,
        };
      } else {
        params = {
          startDate: calendarDate.startDate,
          endDate: calendarDate.endDate,
        };
      }

      switch (value) {
        case HistoricalNotesTabEnum.Sessions:
          dispatch(
            sessionActions.getList({
              params,
              getAccessTokenSilently,
            }),
          );
          break;
        case HistoricalNotesTabEnum.ClientSN:
          dispatch(
            clientSupervisionActions.getList({
              params,
              getAccessTokenSilently,
            }),
          );
          break;
        default:
          break;
      }
    }
  }, [calendarDate, selectedClient, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    // switch tab when clicked
    setValue(newValue);
  };
  const handleDatePickerChange = (date: Date, startDate: Date, endDate: Date) => {
    setDate(date);
    setCalendarDate({ startDate: startDate, endDate: endDate });

    if (selectedClient) {
      setSelectedClient({
        id: selectedClient?.id,
        label: selectedClient?.label,
      });
    } else {
      setSelectedClient(null);
    }
  };

  const handleSelectedClientChange = (selectedClientOption: IOption | null) => {
    setSelectedClient(selectedClientOption);
    if (!selectedClientOption) {
      dispatch(sessionActions.clearSession());
      dispatch(clientSupervisionActions.clearState());
    }
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  if (clientLoading) return <FProgressBar />;

  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
      </Box>
    );
  }

  return (
    <PageContainer dataTestId="historicalNotePageContainer">
      {(sessionLoading || clientSNLoading) && <FProgressBar />}
      <Grid container spacing={2} sx={{ flexGrow: 1, height: '100%', mt: 5 }}>
        {isTablet ? (
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <FBoxContainer
              dataTestId="historicalNoteWeekPickerBox"
              sx={{
                height: 'auto',
                display: 'flex',
                boxShadow: 'none',
                justifyContent: 'center',
                alignItems: 'center',
                ...calendarStyle,
              }}
            >
              <Typography variant="h4" sx={{ mb: 2 }}>
                {t('findNotesByWeek')}
              </Typography>
              <FHWeekDatesPicker
                data-testid="historicalNoteWeekPicker"
                value={date}
                textStyles={{ fontSize: 14 }}
                showMobileView={isMobile}
                disableNavigation={false}
                onChange={(date: Date, startDate: Date, endDate: Date) => {
                  handleDatePickerChange(date, startDate, endDate);
                }}
              />
            </FBoxContainer>
          </Grid>
        ) : null}

        <Grid item xs={12} md={6} lg={7.5} sx={{ height: '100%', mr: '18px' }}>
          <Tabs
            TabIndicatorProps={{ style: { display: 'none' } }}
            value={value}
            onChange={handleChange}
            aria-label="Notes"
            sx={{
              '& button': {
                color: FORTA_WHITE,
                backgroundColor: getThemeColor(Themes.Primary).primary,
                borderTopRightRadius: '10px',
                boxShadow: '0px 4px 4px rgba(18, 78, 120, 0.25)',
                padding: '10px 24px 0px 24px',
              },
              '& button.Mui-selected': {
                color: FORTA_BLACK,
                backgroundColor: getThemeColor(Themes.Primary).light3,
              },
              '& button + button': {
                marginLeft: 0.5,
              },
            }}
          >
            <Tab
              data-testid="historicalSessionNotes"
              label={t('session')}
              {...a11yProps(0)}
              sx={{
                p: 1,
                backgroundColor: getThemeColor(Themes.Primary).light3,
                boxShadow: '0px 4px 4px rgba(18, 78, 120, 0.25)',
                borderTopLeftRadius: '10px',
                boxSizing: 'border-box',
              }}
            />
            {!areUserRolesIn(user?.roles, [Roles.Guardian]) && (
              <Tab
                data-testid="historicalClientNotes"
                label="Client"
                {...a11yProps(1)}
                sx={{
                  p: 1,
                  backgroundColor: getThemeColor(Themes.Primary).light3,
                  boxShadow: '0px 4px 4px rgba(18, 78, 120, 0.25)',
                  borderTopLeftRadius: '10px',
                  boxSizing: 'border-box',
                }}
              />
            )}
          </Tabs>
          <TabPanel value={value} index={HistoricalNotesTabEnum.Sessions}>
            <FBoxContainer
              sx={{
                maxHeight: '80vh',
                overflow: 'auto',
                borderRadius: '0px',
                borderBottomLeftRadius: '10px',
                borderBottomRightRadius: '10px',
                borderTopRightRadius: '10px',
                boxShadow: isTablet ? 'none' : `0px 4px 4px ${FORTA_BLUE.light2}`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  flexGrow: 1,
                  marginBottom: '10px',
                  flexDirection: 'column',
                }}
              >
                <FHAutocomplete
                  sx={{ width: '100%' }}
                  inputHeight={40}
                  value={selectedClient}
                  options={clientListWithAllClientOption}
                  placeholder="Select Client"
                  renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
                    return (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    );
                  }}
                  onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                    handleSelectedClientChange(newValue);
                  }}
                  isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
                />
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: 'auto',
                  height: 'calc(100% - 40px)',
                }}
              >
                {sessions.map((session: IBTSession, index: number) => (
                  <Grid key={session.id} item xs={12} sm={12} lg={6} sx={{ my: 3 }}>
                    <SessionNoteItem key={index} session={{ ...session }} sx={{ maxWidth: 350 }} />
                  </Grid>
                ))}
              </Box>
            </FBoxContainer>
          </TabPanel>
          <TabPanel value={value} index={HistoricalNotesTabEnum.ClientSN}>
            <FBoxContainer
              sx={{
                maxHeight: '80vh',
                overflow: 'auto',
                borderRadius: '0px',
                borderBottomLeftRadius: '10px',
                borderBottomRightRadius: '10px',
                borderTopRightRadius: '10px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  flexGrow: 1,
                  marginBottom: '10px',
                  flexDirection: 'column',
                }}
              >
                <FHAutocomplete
                  sx={{ width: '100%' }}
                  inputHeight={40}
                  placeholder="Select Client"
                  isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
                  options={clientListWithAllClientOption}
                  value={selectedClient}
                  onChange={(_: any, newValue: IOption | null) =>
                    handleSelectedClientChange(newValue)
                  }
                  renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
                    return (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    );
                  }}
                />
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: 'auto',
                  height: 'calc(100% - 40px)',
                }}
              >
                {clientSupervisions.map((supervision: IClientSupervision, index: number) => (
                  <Grid key={supervision.id} item xs={11} sm={11} lg={6} sx={{ my: 3 }}>
                    <SupervisionItem
                      type={SupervisionType.Client}
                      key={index}
                      supervision={supervision}
                      sx={{ maxWidth: 350 }}
                    />
                  </Grid>
                ))}
              </Box>
            </FBoxContainer>
          </TabPanel>
        </Grid>
        {isTablet ? null : (
          <Grid item xs={12} md={5} lg={4}>
            <FBoxContainer
              dataTestId="historicalNoteWeekPickerBox"
              sx={{
                height: 'auto',
                display: 'flex',
                justifyContent: 'center',
                ...calendarStyle,
              }}
            >
              <Box>
                <Typography variant="h4" align="left" sx={{ mb: 2 }}>
                  {t('findNotesByWeek')}
                </Typography>
                <Box
                  sx={{
                    width: '370px',
                  }}
                >
                  <FDatePicker
                    dataTestId="historicalNoteWeekPicker"
                    value={date}
                    onChange={(date: Date, startDate: Date, endDate: Date) =>
                      handleDatePickerChange(date, startDate, endDate)
                    }
                  />
                </Box>
              </Box>
            </FBoxContainer>
          </Grid>
        )}
      </Grid>
    </PageContainer>
  );
}

export default HistoricalNotes;
